import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RoomEvent } from 'livekit-client';
import { useChat, useRoomContext, useTranscriptions, } from '@livekit/components-react';
import { transcriptionToChatMessage } from '@/lib/utils';
const PRODUCT_MARKER = '\n\n<!--PRODUCTS:';
const PRODUCT_PAYLOAD_TOPIC = 'products';
const BOOKING_PAYLOAD_TOPIC = 'booking';
const extractCleanText = (raw) => {
    if (!raw.includes(PRODUCT_MARKER)) {
        return raw.trim();
    }
    return raw.split(PRODUCT_MARKER)[0].trim();
};
const normalizeMessageKey = (raw) => raw
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.,!?]+$/g, '')
    .trim()
    .toLowerCase();
const containsProductMarkers = (raw) => raw.includes('<!--PRODUCTS:');
export default function useChatAndTranscription() {
    const transcriptions = useTranscriptions();
    const chat = useChat();
    const room = useRoomContext();
    const [payloadTick, setPayloadTick] = useState(0);
    // Track locally sent messages for immediate UI display (especially important in voice mode)
    const [localMessages, setLocalMessages] = useState([]);
    const localMessageIdsRef = useRef(new Set());
    // Messages delivered via data channel (e.g., order/payment links in voice mode)
    const [dataMessages, setDataMessages] = useState([]);
    const dataMessageKeysRef = useRef(new Set());
    // Store payloads for voice replies in arrival order
    const pendingPayloadsRef = useRef([]);
    const consumedPayloadSeqsRef = useRef(new Set());
    const consumedPayloadKeysRef = useRef(new Set());
    const nextPayloadSeqRef = useRef(1);
    const payloadByMessageIdRef = useRef(new Map());
    // Maintain first-seen arrival order for stability across clock skew.
    const seenOrderRef = useRef(new Map());
    const nextSeqRef = useRef(1);
    const messageFirstSeenAtRef = useRef(new Map());
    // Track when a message was last seen in the merged array (for graceful cleanup)
    const messageLastSeenAtRef = useRef(new Map());
    const PENDING_PAYLOAD_TTL_MS = 30000;
    const PAYLOAD_ATTACH_SKEW_MS = 2500;
    const PAYLOAD_MAP_GRACE_MS = 10000; // Grace period before removing payload mappings
    const prunePendingPayloads = (now) => {
        if (pendingPayloadsRef.current.length === 0)
            return;
        pendingPayloadsRef.current = pendingPayloadsRef.current.filter((p) => now - p.receivedAt <= PENDING_PAYLOAD_TTL_MS);
    };
    const enqueuePayload = (payload) => {
        if (!payload)
            return;
        prunePendingPayloads(Date.now());
        const cleanText = extractCleanText(payload);
        const sourceKey = normalizeMessageKey(cleanText);
        // Best-effort parse to extract product names for smarter alignment.
        let productNames = [];
        try {
            const markerIdx = payload.indexOf('<!--PRODUCTS:');
            const endIdx = payload.indexOf('-->', markerIdx);
            if (markerIdx !== -1 && endIdx !== -1) {
                const jsonRaw = payload.slice(markerIdx + '<!--PRODUCTS:'.length, endIdx);
                const parsed = JSON.parse(jsonRaw);
                if (Array.isArray(parsed)) {
                    productNames = parsed
                        .map((p) => {
                        try {
                            return String((p && p.name) || '').trim();
                        }
                        catch (_a) {
                            return '';
                        }
                    })
                        .filter((n) => n.length > 0);
                }
            }
        }
        catch (_a) {
            // Parsing failures should never break chat; we just fall back to name-less matching.
            productNames = [];
        }
        const seq = nextPayloadSeqRef.current++;
        pendingPayloadsRef.current.push({
            seq,
            payload,
            sourceKey,
            productNames,
            receivedAt: Date.now(),
        });
        setPayloadTick((v) => v + 1);
    };
    const enqueueDataMessage = (raw) => {
        const text = String(raw || '').trim();
        if (!text)
            return;
        const key = normalizeMessageKey(text);
        if (!key)
            return;
        if (dataMessageKeysRef.current.has(key))
            return;
        dataMessageKeysRef.current.add(key);
        const msg = {
            id: `data-${Date.now()}-${Math.random()}`,
            message: text,
            timestamp: Date.now(),
        };
        setDataMessages((prev) => [...prev, msg]);
    };
    // Reset all local state when room connects to a new session
    // This ensures old messages don't persist across session restarts
    useEffect(() => {
        const handleConnected = () => {
            // Clear local messages and refs for fresh session
            setLocalMessages([]);
            localMessageIdsRef.current.clear();
            setDataMessages([]);
            dataMessageKeysRef.current.clear();
            pendingPayloadsRef.current = [];
            consumedPayloadSeqsRef.current.clear();
            consumedPayloadKeysRef.current.clear();
            nextPayloadSeqRef.current = 1;
            payloadByMessageIdRef.current.clear();
            seenOrderRef.current.clear();
            nextSeqRef.current = 1;
            messageFirstSeenAtRef.current.clear();
            messageLastSeenAtRef.current.clear();
        };
        room.on(RoomEvent.Connected, handleConnected);
        return () => {
            room.off(RoomEvent.Connected, handleConnected);
        };
    }, [room]);
    // Listen for display messages sent via data channel (product payloads)
    useEffect(() => {
        var _a;
        const handleData = (payload, _p, _k, topic) => {
            if (topic !== PRODUCT_PAYLOAD_TOPIC)
                return;
            try {
                const displayText = new TextDecoder().decode(payload);
                enqueuePayload(displayText);
            }
            catch (error) {
                console.error('Failed to parse display message:', error);
            }
        };
        const handleText = (text, _p, topic) => {
            if (topic !== PRODUCT_PAYLOAD_TOPIC)
                return;
            try {
                enqueuePayload(text);
            }
            catch (error) {
                console.error('Failed to parse display text:', error);
            }
        };
        room.on(RoomEvent.DataReceived, handleData);
        // @ts-ignore - Some SDK versions emit TextReceived
        (_a = room.on) === null || _a === void 0 ? void 0 : _a.call(room, RoomEvent.TextReceived, handleText);
        return () => {
            var _a;
            room.off(RoomEvent.DataReceived, handleData);
            // @ts-ignore
            (_a = room.off) === null || _a === void 0 ? void 0 : _a.call(room, RoomEvent.TextReceived, handleText);
        };
    }, [room]);
    // Listen for booking link messages sent via data channel (voice mode)
    // These are published when Calendly tools return booking URLs
    useEffect(() => {
        var _a;
        const handleData = (payload, _p, _k, topic) => {
            if (topic !== BOOKING_PAYLOAD_TOPIC)
                return;
            try {
                const data = JSON.parse(new TextDecoder().decode(payload));
                if ((data === null || data === void 0 ? void 0 : data.type) === 'booking_link' && (data === null || data === void 0 ? void 0 : data.url)) {
                    // Add a message containing the Calendly URL so findLatestCalendlyLink can detect it
                    const displayText = `Book your appointment: ${data.url}`;
                    enqueueDataMessage(displayText);
                }
            }
            catch (error) {
                console.error('Failed to parse booking message:', error);
            }
        };
        const handleText = (text, _p, topic) => {
            if (topic !== BOOKING_PAYLOAD_TOPIC)
                return;
            try {
                const data = JSON.parse(text);
                if ((data === null || data === void 0 ? void 0 : data.type) === 'booking_link' && (data === null || data === void 0 ? void 0 : data.url)) {
                    const displayText = `Book your appointment: ${data.url}`;
                    enqueueDataMessage(displayText);
                }
            }
            catch (error) {
                console.error('Failed to parse booking text:', error);
            }
        };
        room.on(RoomEvent.DataReceived, handleData);
        // @ts-ignore - Some SDK versions emit TextReceived
        (_a = room.on) === null || _a === void 0 ? void 0 : _a.call(room, RoomEvent.TextReceived, handleText);
        return () => {
            var _a;
            room.off(RoomEvent.DataReceived, handleData);
            // @ts-ignore
            (_a = room.off) === null || _a === void 0 ? void 0 : _a.call(room, RoomEvent.TextReceived, handleText);
        };
    }, [room]);
    // Clean up local messages that have appeared in chat.chatMessages
    useEffect(() => {
        const chatMessageTexts = new Set(chat.chatMessages
            .filter((m) => { var _a; return (_a = m.from) === null || _a === void 0 ? void 0 : _a.isLocal; })
            .map((m) => normalizeMessageKey(String(m.message || ''))));
        setLocalMessages((prev) => prev.filter((msg) => {
            const msgText = normalizeMessageKey(String(msg.message || ''));
            const isDuplicate = chatMessageTexts.has(msgText);
            if (isDuplicate) {
                localMessageIdsRef.current.delete(msg.id);
            }
            return !isDuplicate;
        }));
    }, [chat.chatMessages]);
    // Custom send function that adds message locally for immediate UI display
    const sendWithLocalEcho = useCallback(async (text) => {
        var _a;
        const trimmed = text.trim();
        if (!trimmed)
            return;
        console.log('[useChatAndTranscription] sendWithLocalEcho called with:', trimmed);
        // Create a local message immediately for UI display
        const localMsg = {
            id: `local-${Date.now()}-${Math.random()}`,
            message: trimmed,
            timestamp: Date.now(),
            from: (_a = room.localParticipant) !== null && _a !== void 0 ? _a : undefined,
        };
        console.log('[useChatAndTranscription] Created local message:', localMsg);
        // Add to local messages
        localMessageIdsRef.current.add(localMsg.id);
        setLocalMessages((prev) => {
            const updated = [...prev, localMsg];
            console.log('[useChatAndTranscription] Updated local messages:', updated.length);
            return updated;
        });
        // Send via LiveKit chat
        await chat.send(trimmed);
    }, [chat, room.localParticipant]);
    const mergedTranscriptions = useMemo(() => {
        var _a;
        const chatMessages = chat.chatMessages;
        const transcriptionMessages = transcriptions.map((t) => transcriptionToChatMessage(t, room));
        const payloadMap = payloadByMessageIdRef.current;
        const now = Date.now();
        prunePendingPayloads(now);
        const enrichAgentMessage = (msg) => {
            var _a, _b, _c;
            const msgKey = String(msg.id);
            if (!messageFirstSeenAtRef.current.has(msgKey)) {
                messageFirstSeenAtRef.current.set(msgKey, Date.now());
            }
            const firstSeenAt = (_a = messageFirstSeenAtRef.current.get(msgKey)) !== null && _a !== void 0 ? _a : Date.now();
            const isAgentMessage = !((_c = (_b = msg.from) === null || _b === void 0 ? void 0 : _b.isLocal) !== null && _c !== void 0 ? _c : false);
            const messageStr = String(msg.message || '');
            const hasMarkers = containsProductMarkers(messageStr);
            if (!isAgentMessage) {
                return msg;
            }
            if (hasMarkers) {
                const normalized = normalizeMessageKey(extractCleanText(messageStr));
                if (normalized) {
                    const pendingList = pendingPayloadsRef.current;
                    for (let i = 0; i < pendingList.length; i += 1) {
                        const p = pendingList[i];
                        if (now - p.receivedAt > PENDING_PAYLOAD_TTL_MS)
                            continue;
                        if (p.sourceKey && p.sourceKey === normalized) {
                            pendingList.splice(i, 1);
                            consumedPayloadSeqsRef.current.add(p.seq);
                            consumedPayloadKeysRef.current.add(p.sourceKey);
                            break;
                        }
                    }
                }
                return msg;
            }
            // If we've already attached a payload to this message, always reuse it.
            if (payloadMap.has(msgKey)) {
                const existing = payloadMap.get(msgKey);
                const rawText = String(msg.message || '').trim();
                const markerStart = existing.payload.indexOf('<!--PRODUCTS:');
                const mergedMessage = markerStart >= 0
                    ? `${rawText}\n\n${existing.payload.slice(markerStart)}`
                    : `${rawText}\n\n${existing.payload}`;
                return Object.assign(Object.assign({}, msg), { message: mergedMessage });
            }
            const rawText = String(msg.message || '').trim();
            if (!rawText) {
                return msg;
            }
            const lowerText = rawText.toLowerCase();
            const pendingList = pendingPayloadsRef.current;
            const isEligibleByTiming = (p) => now - p.receivedAt <= PENDING_PAYLOAD_TTL_MS &&
                firstSeenAt + PAYLOAD_ATTACH_SKEW_MS >= p.receivedAt;
            // Prefer a payload whose product names appear in the message; otherwise,
            // fall back to the earliest payload that arrived before this message.
            let matchedIdx = -1;
            for (let i = 0; i < pendingList.length; i += 1) {
                const p = pendingList[i];
                if (!isEligibleByTiming(p))
                    continue;
                if (!p.productNames || p.productNames.length === 0)
                    continue;
                const hit = p.productNames.some((name) => {
                    const ln = name.toLowerCase();
                    if (!ln)
                        return false;
                    return lowerText.includes(ln);
                });
                if (hit) {
                    matchedIdx = i;
                    break;
                }
            }
            if (matchedIdx === -1) {
                for (let i = 0; i < pendingList.length; i += 1) {
                    const p = pendingList[i];
                    if (!isEligibleByTiming(p))
                        continue;
                    matchedIdx = i;
                    break;
                }
            }
            if (matchedIdx === -1)
                return msg;
            const [pending] = pendingList.splice(matchedIdx, 1);
            payloadMap.set(msgKey, pending);
            consumedPayloadSeqsRef.current.add(pending.seq);
            if (pending.sourceKey) {
                consumedPayloadKeysRef.current.add(pending.sourceKey);
            }
            // Preserve the natural-language reply text, but append the product markers
            // so the formatter can render badges under the same message.
            const markerStart = pending.payload.indexOf('<!--PRODUCTS:');
            const mergedMessage = markerStart >= 0
                ? `${rawText}\n\n${pending.payload.slice(markerStart)}`
                : `${rawText}\n\n${pending.payload}`;
            return Object.assign(Object.assign({}, msg), { message: mergedMessage });
        };
        const enrichedChatMessages = chatMessages.map(enrichAgentMessage);
        const enrichedTranscriptions = transcriptionMessages.map(enrichAgentMessage);
        const filteredChatMessages = enrichedChatMessages;
        // Assign a stable arrival index on first sight. Prioritize local chat messages
        // when both appear in the same render to preserve natural Q→A order.
        const assignSeq = (msg) => {
            const map = seenOrderRef.current;
            if (!map.has(msg.id)) {
                map.set(msg.id, nextSeqRef.current++);
            }
        };
        // Prefer assigning sequence to chat messages first, then transcriptions, then local messages.
        filteredChatMessages.forEach(assignSeq);
        enrichedTranscriptions.forEach(assignSeq);
        dataMessages.forEach(assignSeq);
        localMessages.forEach(assignSeq);
        const merged = [
            ...filteredChatMessages,
            ...enrichedTranscriptions,
            ...dataMessages,
            ...localMessages,
        ];
        console.log('[useChatAndTranscription] Merged messages:', {
            chat: filteredChatMessages.length,
            transcriptions: enrichedTranscriptions.length,
            local: localMessages.length,
            total: merged.length,
        });
        // Best-effort cleanup: drop payload mappings for messages that have been
        // missing for longer than the grace period. This prevents premature cleanup
        // when messages temporarily disappear due to re-renders or LiveKit updates.
        const allIds = new Set(merged.map((m) => String(m.id)));
        // Update last-seen timestamps for messages currently in the merged array
        for (const id of allIds) {
            messageLastSeenAtRef.current.set(id, now);
        }
        // Only delete entries that have been missing for longer than the grace period
        for (const key of payloadMap.keys()) {
            if (!allIds.has(key)) {
                const lastSeen = (_a = messageLastSeenAtRef.current.get(key)) !== null && _a !== void 0 ? _a : 0;
                if (now - lastSeen > PAYLOAD_MAP_GRACE_MS) {
                    payloadMap.delete(key);
                    messageFirstSeenAtRef.current.delete(key);
                    messageLastSeenAtRef.current.delete(key);
                }
            }
        }
        const getSeq = (msg) => { var _a; return (_a = seenOrderRef.current.get(msg.id)) !== null && _a !== void 0 ? _a : Number.MAX_SAFE_INTEGER; };
        return merged.sort((a, b) => {
            var _a, _b, _c, _d;
            const seqDelta = getSeq(a) - getSeq(b);
            if (seqDelta !== 0 && Number.isFinite(seqDelta)) {
                return seqDelta;
            }
            const dt = a.timestamp - b.timestamp;
            if (dt !== 0) {
                return dt;
            }
            const aIsUser = (_b = (_a = a.from) === null || _a === void 0 ? void 0 : _a.isLocal) !== null && _b !== void 0 ? _b : false;
            const bIsUser = (_d = (_c = b.from) === null || _c === void 0 ? void 0 : _c.isLocal) !== null && _d !== void 0 ? _d : false;
            if (aIsUser !== bIsUser) {
                return aIsUser ? -1 : 1;
            }
            return String(a.id).localeCompare(String(b.id));
        });
    }, [transcriptions, chat.chatMessages, room, localMessages, dataMessages, payloadTick]);
    return { messages: mergedTranscriptions, send: sendWithLocalEcho };
}
